import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Layers, Loader2, Mail, AlertTriangle, ExternalLink, FileDown } from 'lucide-react';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => `${v < 0 ? '−' : ''}A$${Math.round(Math.abs(v || 0)).toLocaleString()}`;

/** Which project factors add the most price and margin across open deals. */
export function PortfolioFactorCard({ filterParams = {}, filters = null }) {
  const [data, setData] = useState(null);
  const [packing, setPacking] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const downloadPack = async () => {
    setPacking(true);
    try {
      const r = await axios.get(`${API_URL}/api/insights/margin/deal-pack.pdf`, { params: filterParams, withCredentials: true, responseType: 'blob' });
      saveAs(r.data, `Deal_Review_Pack_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { toast.error('Could not build the deal review pack'); } finally { setPacking(false); }
  };
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/factors`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  const rows = (data?.factors || []).filter((f) => f.deals > 0);
  const maxAbs = Math.max(1, ...rows.map((f) => Math.abs(f.price_aud)));
  return (
    <Card className="border-slate-200" data-testid="portfolio-factor-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-[#4ABFCF]" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Price &amp; margin by project factor <span className="text-[10px] font-normal text-slate-400">· all open deals (page filters not applied)</span></h3>
          {data && !data.error && <span className="text-[11px] text-slate-400" data-testid="portfolio-factor-open-count">· {data.open_deals} open deals</span>}
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={downloadPack} disabled={packing} title="One PDF with a deal summary page for every open deal" data-testid="deal-pack-download-btn">
            {packing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FileDown className="w-3 h-3 mr-1" /> Deal review pack</>}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEmailOpen(true)} title="Email the pack to selected recipients" data-testid="deal-pack-email-btn">
            <Mail className="w-3 h-3 mr-1" /> Email pack
          </Button>
          <DealPackEmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} filters={filters} />
        </div>
        <p className="text-xs text-slate-500 mb-3">Uplift each project factor adds across all open deals versus baseline scope (A$).</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">Could not load factor view.</p>}
        {data && !data.error && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="portfolio-factor-empty">No open deals carry a non-neutral project factor yet.</p>}
        {rows.length > 0 && (
          <div className="space-y-2">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-wide text-slate-400 px-1">
              <span className="col-span-4">Factor</span><span className="col-span-1 text-right">Deals</span><span className="col-span-1 text-right">Avg ×</span>
              <span className="col-span-2 text-right">Price</span><span className="col-span-2 text-right">Margin</span><span className="col-span-2 text-right">Margin %</span>
            </div>
            {rows.map((f) => (
              <div key={f.key} className="relative grid grid-cols-12 items-center text-xs px-1 py-1 rounded" data-testid={`portfolio-factor-${f.key}`}>
                <div className="absolute left-0 top-0 bottom-0 bg-[#4ABFCF]/10 rounded" style={{ width: `${Math.abs(f.price_aud) / maxAbs * 100}%` }} />
                <span className="col-span-4 relative font-medium text-slate-700">{f.label}</span>
                <span className="col-span-1 relative text-right tabular-nums">{f.deals}</span>
                <span className="col-span-1 relative text-right tabular-nums">{f.avg_multiplier.toFixed(2)}</span>
                <span className="col-span-2 relative text-right tabular-nums font-semibold text-[#0F2040]">{fmtA(f.price_aud)}</span>
                <span className={`col-span-2 relative text-right tabular-nums ${f.margin_aud < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtA(f.margin_aud)}</span>
                <span className="col-span-2 relative text-right tabular-nums text-slate-600">{f.margin_pct == null ? '—' : `${f.margin_pct}%`}</span>
              </div>
            ))}
            <p className="text-[10px] text-slate-400 pt-1">Baseline scope across open deals: {fmtA(data.baseline.price_aud)} price · {fmtA(data.baseline.cost_aud)} cost.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Open estimates whose saved price drifted from today's rate cards, with the weekly digest trigger. */
export function RateDriftCard({ canSend }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/drift`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/send-drift-digest`, {}, { withCredentials: true });
      if (r.data.sent) toast.success(`Drift digest sent to ${r.data.recipients} leadership recipient(s)`);
      else toast.info(`Digest not sent — ${r.data.reason}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send digest'); } finally { setSending(false); }
  };
  const rows = data?.rows || [];
  return (
    <Card className={rows.length ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'} data-testid="rate-drift-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Rate-card drift <span className="text-[10px] font-normal text-slate-400">· rates, not deals (page filters not applied)</span></h3>
          <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${rows.length ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'}`} data-testid="rate-drift-count">{data ? rows.length : '…'}</span>
          {canSend && (
            <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={send} disabled={sending || !rows.length} data-testid="rate-drift-send-digest-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 mr-1" /> Send digest now</>}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">Open estimates whose saved price differs by more than {data?.threshold_pct ?? 2}% from today's rate cards. Leadership receives this list by email every week.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data && !data.error && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="rate-drift-empty">All open estimates match today's rate cards.</p>}
        {rows.length > 0 && (
          <ul className="divide-y divide-amber-100">
            {rows.slice(0, 15).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-1.5 text-xs" data-testid={`rate-drift-row-${r.id}`}>
                <div className="min-w-0">
                  <a href={`/estimator?load=${r.id}`} className="font-medium text-[#0F2040] hover:underline inline-flex items-center gap-1 truncate">{r.name}<ExternalLink className="w-3 h-3 text-slate-400" /></a>
                  <span className="text-slate-400"> · {r.organization_name || '—'} · {String(r.status).replace(/_/g, ' ')}{r.locked ? ' · locked' : ''}</span>
                </div>
                <div className="text-right shrink-0 tabular-nums">
                  <span className="text-slate-500">{r.currency_code} {Math.round(r.saved_cost).toLocaleString()} → </span>
                  <b className="text-[#0F2040]">{Math.round(r.live_cost).toLocaleString()}</b>
                  <span className={`ml-2 font-semibold ${r.move_pct > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{r.move_pct > 0 ? '+' : ''}{r.move_pct}%</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Open deals whose project factors sit well above the portfolio average — flagged before a quote goes out. */
export function BenchmarkAlertsCard({ onOpenDeal }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/benchmark-alerts`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  const rows = data?.rows || [];
  return (
    <Card className={data?.unquoted_count ? 'border-violet-300 bg-violet-50/30' : 'border-slate-200'} data-testid="benchmark-alerts-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Benchmark alerts</h3>
          <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${data?.unquoted_count ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-testid="benchmark-alerts-count">{data ? data.unquoted_count : '…'}</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">Open deals with a project factor ≥ {data?.delta ?? 0.15} above the portfolio average (across {data?.open_deals ?? '—'} open deals). Not-yet-quoted deals lead so they can be reviewed before a quote is issued.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">Could not load benchmark alerts.</p>}
        {data && !data.error && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="benchmark-alerts-empty">No open deal sits far above the portfolio's typical project factors.</p>}
        {rows.length > 0 && (
          <ul className="divide-y divide-violet-100">
            {rows.slice(0, 12).map((r) => (
              <li key={r.id} className="py-1.5 flex items-start justify-between gap-3 text-xs" data-testid={`benchmark-alert-row-${r.id}`}>
                <div className="min-w-0">
                  <button type="button" onClick={() => onOpenDeal?.(r.id)} className="font-medium text-[#0F2040] hover:underline truncate text-left" data-testid={`benchmark-alert-open-${r.id}`}>{r.name}</button>
                  <span className="text-slate-400"> · {r.organization_name || '—'} · {String(r.status).replace(/_/g, ' ')}</span>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {r.flags.map((f) => (
                      <span key={f.key} className="text-[10px] px-1.5 py-0 rounded border bg-white border-violet-200 text-violet-800" title={`Portfolio average ×${f.portfolio_avg_multiplier.toFixed(2)}`}>
                        {f.label} ×{f.multiplier.toFixed(2)} <b>(+{f.vs_portfolio.toFixed(2)})</b>
                      </span>
                    ))}
                  </div>
                </div>
                <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.quoted ? 'bg-slate-100 text-slate-500' : 'bg-violet-600 text-white'}`} data-testid={`benchmark-alert-status-${r.id}`}>{r.quoted ? 'quoted' : 'not quoted'}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Email the deal review pack (PDF attachment) to chosen recipients. */
export function DealPackEmailDialog({ open, onClose, filters = null }) {
  const [recipients, setRecipients] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/deal-pack/email`, { recipients, note, filters: filters || {} }, { withCredentials: true });
      toast.success(`Deal review pack emailed to ${r.data.recipients.length} recipient(s)`);
      setRecipients(''); setNote(''); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not email the deal review pack'); } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent className="sm:max-w-[480px]" data-testid="deal-pack-email-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">Email deal review pack</DialogTitle>
          <DialogDescription>Sends the current pack ({filters && Object.values(filters).some((v) => v?.length) ? 'open deals matching the page filters' : 'all open deals'}) as a PDF attachment. Confidential — leadership recipients only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="deal-pack-recipients" className="text-xs">Recipients <span className="text-slate-400">· comma or space separated</span></Label>
            <Textarea id="deal-pack-recipients" rows={2} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="cro@company.com, cco@company.com" className="text-sm mt-1 resize-none" data-testid="deal-pack-recipients-input" />
          </div>
          <div>
            <Label htmlFor="deal-pack-note" className="text-xs">Note <span className="text-slate-400">· optional</span></Label>
            <Textarea id="deal-pack-note" rows={3} value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} placeholder="Context for this week's deal review…" className="text-sm mt-1 resize-none" data-testid="deal-pack-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="deal-pack-email-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending || !recipients.trim()} className="bg-[#0F2040] hover:bg-[#1a2f5a] text-white" data-testid="deal-pack-email-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1.5" /> Send pack</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
